/**
 * Walker model: race × gender × class, rendered from four sprite sheets
 * (public/sprites/<race>.png). The combination is stored on the user row as
 * `avatar_id`, e.g. "hobbit-m-wanderer".
 */
export const RACES = [
  ["hobbit", "Hobbit"],
  ["human", "Human"],
  ["elf", "Elf"],
  ["dwarf", "Dwarf"],
] as const;
export const GENDERS = [
  ["f", "Feminine"],
  ["m", "Masculine"],
] as const;
export const CLASSES = [
  ["wanderer", "Wanderer"],
  ["ranger", "Ranger"],
  ["warrior", "Warrior"],
  ["wizard", "Wizard"],
] as const;

export type Race = (typeof RACES)[number][0];
export type Gender = (typeof GENDERS)[number][0];
export type WalkerClass = (typeof CLASSES)[number][0];

export interface Walker {
  race: Race;
  gender: Gender;
  cls: WalkerClass;
}

export const DEFAULT_WALKER: Walker = { race: "hobbit", gender: "m", cls: "wanderer" };
export const DEFAULT_AVATAR_ID = walkerId(DEFAULT_WALKER);

const isRace = (v: string): v is Race => RACES.some((r) => r[0] === v);
const isGender = (v: string): v is Gender => GENDERS.some((g) => g[0] === v);
const isClass = (v: string): v is WalkerClass => CLASSES.some((c) => c[0] === v);

export function walkerId(w: Walker): string {
  return `${w.race}-${w.gender}-${w.cls}`;
}

/** Strict parse; null if the id is not a valid combination. */
export function parseWalkerId(id: unknown): Walker | null {
  if (typeof id !== "string") return null;
  const [race, gender, cls] = id.split("-");
  if (race && gender && cls && isRace(race) && isGender(gender) && isClass(cls)) return { race, gender, cls };
  return null;
}

/** Ids from the first avatar list, mapped to the nearest walker. */
const LEGACY: Record<string, Walker> = {
  "hobbit-gardener": { race: "hobbit", gender: "m", cls: "wanderer" },
  "hobbit-ringbearer": { race: "hobbit", gender: "m", cls: "wanderer" },
  "hobbit-knight": { race: "hobbit", gender: "m", cls: "warrior" },
  "hobbit-guard": { race: "hobbit", gender: "m", cls: "warrior" },
  ranger: { race: "human", gender: "m", cls: "ranger" },
  shieldmaiden: { race: "human", gender: "f", cls: "warrior" },
  "captain-of-gondor": { race: "human", gender: "m", cls: "warrior" },
  "elf-archer": { race: "elf", gender: "m", cls: "ranger" },
  "elf-lady": { race: "elf", gender: "f", cls: "wizard" },
  "dwarf-axe": { race: "dwarf", gender: "m", cls: "warrior" },
  "grey-wizard": { race: "human", gender: "m", cls: "wizard" },
};

/** Lenient parse for display: legacy ids map to a walker, anything else is the default. */
export function walkerFromId(id: unknown): Walker {
  const strict = parseWalkerId(id);
  if (strict) return strict;
  return (typeof id === "string" && LEGACY[id]) || DEFAULT_WALKER;
}

export const isAvatarId = (id: unknown): id is string => parseWalkerId(id) !== null;

export function walkerTitle(w: Walker): string {
  const race = RACES.find((r) => r[0] === w.race)?.[1] ?? "Hobbit";
  const cls = CLASSES.find((c) => c[0] === w.cls)?.[1] ?? "Wanderer";
  return `${race} ${cls}`;
}
export function walkerSubtitle(w: Walker): string {
  return GENDERS.find((g) => g[0] === w.gender)?.[1] ?? "Masculine";
}
